import { getKeys } from "@base/util/TypeUtil";
import { Access } from "@biz/definitions/AccessDefinitions";
import { ArticleAvailability, getAvailability, IArticle, IArticleColorSmall, IArticleGroup } from "./definitions/ArticleDefinitions";
import { ClothType } from "./definitions/SunSailDefinitions";

export interface IColorItemOverridable {
  color?: string;
  priority: number;
  availability?: ArticleAvailability;
}

export const overridableColorItemsProps: (keyof IColorItemOverridable)[] = ["priority", "color", "availability"]
export const defaultOverridable: IColorItemOverridable = {
  color: "#ffffff",
  priority: 1000,
  availability: "available",
}

export type ClothPatterns = "p1" | "p2";
export type ProductTextures = "nano" | "wood_mid";
export interface IColorItem extends IColorItemOverridable {
  _id: string;
  id: string;
  print?: boolean;
  style?: boolean;
  pattern?: ClothPatterns;
  name: string;
  displayName: string;
  description?: string;
  specialColor?: boolean;
  overrideId?: string;
  rollWidth?: number;
  deleted?: number;
  group: string;
}

export interface IColorWithId {
  id: string
}

export interface IColorWithVariantId {
  variantId: string
}

// defaultMaterialUsageWidth: 1660
export const clothInfos: Record<string, { name: string; clothType: ClothType; disabled?: boolean; access?: Access, priority: number, defaultMaterialUsageWidth: number | ((params: { isStyle: boolean, isSpecialColor: boolean }) => number) }> = {
  "201": { name: 'Silvanet', clothType: ClothType.Silvanet, priority: 190, defaultMaterialUsageWidth: 3900 },
  "393": { name: 'Lotus', clothType: ClothType.Lotus, priority: 20 , defaultMaterialUsageWidth: 1660},
  "701": { name: 'Acrillia', clothType: ClothType.Acrillia, priority: 230, defaultMaterialUsageWidth: 1660 },
  "792": { name: 'Soltis 92', clothType: ClothType.Soltis92, priority: 80, defaultMaterialUsageWidth: ({isSpecialColor}) => isSpecialColor ? 1730 : 2630 },
  "840": { name: 'Finoo', clothType: ClothType.Finoo, priority: 200, defaultMaterialUsageWidth: 1660 },
  "841": { name: 'Finoo Pro', clothType: ClothType.FinooPro, priority: 210, defaultMaterialUsageWidth: 1660 },
  "842": { name: 'Telina', clothType: ClothType.Telina, priority: 220, defaultMaterialUsageWidth: 1660 },
  "920": { name: 'Screen 20', clothType: ClothType.Austrosail_Screen20, priority: 140, defaultMaterialUsageWidth: 2960 },
  "922": { name: 'Screen 22', clothType: ClothType.Austrosail_Screen22, priority: 150, defaultMaterialUsageWidth: 2960 },
  "931": { name: 'Prestar', clothType: ClothType.Prestar, access: Access.SolidayPro, priority: 40, defaultMaterialUsageWidth: 1660 },
  "932": { name: 'Austronet 932', clothType: ClothType.Austronet932, priority: 100, defaultMaterialUsageWidth: 3900 },
  "934": { name: 'Austrosail Acryl', clothType: ClothType.Austrosail_Acryl, priority: 60, defaultMaterialUsageWidth: 1490 },
  "935": { name: 'Austrosail PES', clothType: ClothType.Austrosail_PES, priority: 30, defaultMaterialUsageWidth: 1660 },
  "936": { name: 'Austrosail Nano', clothType: ClothType.Austrosail_Nano, priority: 10, defaultMaterialUsageWidth: ({isStyle}) => isStyle ? 1160 : 1660 },
  "939": { name: 'Chill-Sail', clothType: ClothType.ChillSail, priority: 150, defaultMaterialUsageWidth: 1660 },
  "940": { name: 'Austronet 940', clothType: ClothType.Austronet940, disabled: true, priority: 110, defaultMaterialUsageWidth: 3900 },
  "950": { name: 'Austronet 950', clothType: ClothType.Austronet950, priority: 120, defaultMaterialUsageWidth: 3900 },
  "960": { name: 'Austronet 960', clothType: ClothType.Austronet960, priority: 130, defaultMaterialUsageWidth: 1660 }, // ?
  "961": { name: 'Austrosail Stretch', clothType: ClothType.Austronet_Stretch, access: Access.SolidayPro, priority: 70, defaultMaterialUsageWidth: 1660 }, // ??? 60600A 60600B
  "980": { name: 'Austrosail Concept', clothType: ClothType.Concept, access: Access.SolidayPro, priority: 50, defaultMaterialUsageWidth: 1460 },
  "986": { name: 'Soltis 86', clothType: ClothType.Soltis86, disabled: true, priority: 170, defaultMaterialUsageWidth: ({isSpecialColor}) => isSpecialColor ? 1730 : 2630 },
  "992": { name: 'Austronet 92 (Soltis 92 B-Qualität)', clothType: ClothType.Austronet92, disabled: true, priority: 180, defaultMaterialUsageWidth: 1660 },
  "996": { name: 'Soltis W96', clothType: ClothType.SoltisW96, priority: 90, defaultMaterialUsageWidth: 2630 },
}

export const clothGroups = getKeys(clothInfos).sort((a, b) => clothInfos[a].priority - clothInfos[b].priority);

export class SunSailColors {
  static getColor(colorId: string | undefined, map: Record<string, IColorItem>): IColorItem {
    // if (colorId.startsWith("5"))
    //   console.log("ColorId: ", colorId);
    return map[colorId || ""] || defaultOverridable;
  }

  static getColorId(color: IColorWithId | IColorWithVariantId): string {
    return (color as IColorWithId).id || (color as IColorWithVariantId).variantId;
  }

  static getColorNameParts(color: {
    name: string;
    overrideId?: string;
    specialColor?: boolean;
  } & (IColorWithId | IColorWithVariantId)
  ): { specialColor: boolean; name: string; id: any; } {
    return {
      specialColor: !!color.specialColor,
      name: color.name,
      id: color.overrideId ? color.overrideId : (SunSailColors.getColorId(color))
    }
  }

  static articleColorToColorItem(ac: IArticleColorSmall, group: string, overrideable: IColorItemOverridable): IColorItem {
    const colorItem: IColorItem = {
      _id: ac.variantId,
      id: ac.variantId,
      print: ac.print,
      style: ac.style,
      pattern: ac.pattern,
      name: ac.name,
      specialColor: ac.specialColor,
      rollWidth: ac.rollWidth,
      displayName: ac.displayName,
      description: ac.description,
      overrideId: ac.overrideId,

      group,
      ...overrideable
    }
    return colorItem;
  }

  static isValidColorId(id: string): boolean {
    return !id.endsWith("999") && !id.endsWith("998") && !id.endsWith("PRNT")
  }

  static getClothByGroupId(id: string): { name: string; clothType: ClothType; disabled?: boolean } {
    return clothInfos[id];
  }

  static calcClothMapFromArticleMap(articleMap: Record<string, IArticle>, colorMap: Record<string, IColorItem>, access: Access):
    Record<string, IArticleGroup<IArticleColorSmall> & { articleIds: string[] }> {
    const clothes: Record<string, IArticleGroup<IArticleColorSmall> & { articleIds: string[] }> = {};
    for (const k of getKeys(articleMap)) {
      const article = articleMap[k];
      if (article.colorGroup && article.colorGroup.items.length > 0) {
        const groupId = article.colorGroup.groupId;
        const clothAccess = clothInfos[groupId]?.access;
        if (clothAccess && clothAccess > access)
          continue;

        const clothInfo = this.getClothByGroupId(groupId);
        if (clothInfo?.disabled)
          continue;
        // console.log("ColorGroup: ", article._id, article.name, groupId, article.colorGroup.text)
        const existing = clothes[groupId];
        if (!existing) {
          clothes[groupId] = {
            ...article.colorGroup,
            items: [...article.colorGroup.items],
            articleIds: [article._id]
          }
        } else {
          if (existing.text !== article.colorGroup.text) {
            console.log("Text differs: ", existing.text, article.colorGroup.text)
          }
          existing.articleIds.push(article._id);
          for (const c of article.colorGroup.items) {
            const found = existing.items.findIndex((item) => item.variantId === c.variantId);
            if (found === -1) {
              existing.items.push(c);
              // console.log("Add missing: ", c, k);
            }
          }
        }
      }
    }

    for (const k of getKeys(clothes)) {
      clothes[k].items.sort((a, b) => (SunSailColors.getColor(a.variantId, colorMap).priority - SunSailColors.getColor(b.variantId, colorMap).priority));
    }

    return clothes;
  }

  static calcClothMapFromArticleGroups(colorMap: Record<string, IColorItem>): Record<string, IColorItem[]> {
    const colorIds = Object.keys(colorMap);
    const clothes: Record<string, IColorItem[]> = {};
    for (const c of colorIds) {
      const clothType = c.substring(0, 3);
      const foundItems = clothes[clothType];
      const items = foundItems || [];
      if (!foundItems)
        clothes[clothType] = items;
      items.push(colorMap[c]);
    }

    for (const k of getKeys(clothes)) {
      clothes[k].sort((a, b) => a.priority - b.priority)
    }

    return clothes;
  }

  static sortColorsByPriority(colorIds: string[], colorMap: Record<string, IColorItem>): string[] {
    const sortedColors = colorIds.sort((a, b) => {
      const ca = colorMap[a];
      const cb = colorMap[b];
      return (ca?.priority !== undefined ? ca.priority : Number.MAX_VALUE) - (cb?.priority !== undefined ? cb.priority : Number.MAX_VALUE)
    })
    return sortedColors;
  }

  static getFirstAvailableColor(colors: string[], colorMap: Record<string, IColorItem>): string {
    const sortedColors = SunSailColors.sortColorsByPriority(colors, colorMap);
    for (const sc of sortedColors) {
      const c = colorMap[sc];
      const av = getAvailability(c?.availability);
      if (av === "available") {
        return sc;
      }
    }
    return sortedColors.length > 0 ? sortedColors[0] : "";
  }
}

export interface ISunSailColorProvider {
  reload(): Promise<void>;
  getColorById(colorId: string | undefined): IColorItem | undefined;
}

export let SunSailColorProvider: ISunSailColorProvider;

export function initSunSailColorProvider(provider: ISunSailColorProvider): void {
  SunSailColorProvider = provider;
}

export function getRollWidth(colorId: string | undefined): number {
  return SunSailColorProvider.getColorById(colorId || "")?.rollWidth || 0
}
